import { AppImage as Image } from "@/shared/ui/AppImage";
import { cn } from "@/shared/lib/cn";
import { inputClass } from "../FormField";

type ConversationListProps = {
  threads: MessageThread[];
  selectedId: string | null;
  search: string;
  filter: "all" | "unread";
  labels: {
    searchPlaceholder: string;
    filters: { all: string; unread: string };
    empty: string;
  };
  onSearchChange: (value: string) => void;
  onFilterChange: (filter: "all" | "unread") => void;
  onSelect: (threadId: string) => void;
};

export function ConversationList({
  threads,
  selectedId,
  search,
  filter,
  labels,
  onSearchChange,
  onFilterChange,
  onSelect,
}: ConversationListProps) {
  return (
    <div className="flex h-full min-h-0 flex-col border-r border-border">
      <div className="space-y-3 border-b border-border px-4 py-4">
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={labels.searchPlaceholder}
          className={inputClass}
        />
        <div className="flex gap-2">
          {(["all", "unread"] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onFilterChange(key)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                filter === key
                  ? "bg-primary text-white"
                  : "bg-card-muted text-muted-foreground hover:bg-card-muted/80",
              )}
            >
              {labels.filters[key]}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted">{labels.empty}</p>
        ) : (
          threads.map((thread) => {
            const isSelected = thread.id === selectedId;
            return (
              <button
                key={thread.id}
                type="button"
                onClick={() => onSelect(thread.id)}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-card-muted/40",
                  isSelected && "bg-primary-soft/60",
                )}
              >
                <div className="relative shrink-0">
                  {thread.avatar ? (
                    <Image
                      src={thread.avatar}
                      alt={thread.name}
                      width={44}
                      height={44}
                      className="h-11 w-11 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                      {thread.avatarFallback}
                    </div>
                  )}
                  {thread.online && (
                    <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-foreground">{thread.name}</p>
                    <span className="shrink-0 text-[11px] text-muted">{thread.lastMessageAt}</span>
                  </div>
                  <p className="truncate text-xs text-muted">{thread.subtitle}</p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <p className="truncate text-xs text-muted-foreground">{thread.lastMessage}</p>
                    {thread.unreadCount > 0 && (
                      <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-white">
                        {thread.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
